// components/steps/Step3ApproverReviewerSelection.tsx - Approver & Reviewer Selection Step Component

import React, { useState } from 'react';
import { ApproverSelection, ReviewerSelection } from '../../types/GatePassTypes';
import { ApproverSelectionPopup } from '../../../workFlow/ApproverSelectionPopup';
import { ReviewerSelectionPopup } from '../../../workFlow/ReviewerSelectionPopup';
import './step3.css';

interface Step3ApproverReviewerSelectionProps {
  formData: {
    gateId?: string;
    visitorId?: string;
    approverId?: string;
    reviewerId?: string;
    consignmentDetails?: string;
    detailedDescription?: string;
  };
  approverSelection?: ApproverSelection | null;
  reviewerSelection?: ReviewerSelection | null;
  regionId?: number | null | undefined;
  userCompanyId?: number;
  getAccessToken: () => Promise<string>;
  onStatusUpdate: (status: { type: 'success' | 'error' | null; message: string }) => void;
  onApproverChange?: (approver: ApproverSelection | null) => void;
  onReviewerChange?: (reviewer: ReviewerSelection | null) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onValidationChange?: (isValid: boolean) => void;
}

// SVG Icon Components for better reusability and maintainability





export const Step3ApproverReviewerSelection: React.FC<Step3ApproverReviewerSelectionProps> = ({
  formData,
  approverSelection = null,
  reviewerSelection = null,
  regionId,
  userCompanyId,
  getAccessToken,
  onStatusUpdate,
  onApproverChange,
  onReviewerChange,
  onChange,
  onValidationChange
}) => {
  // Popup states
  const [showApproverSelection, setShowApproverSelection] = useState(false);
  const [showReviewerSelection, setShowReviewerSelection] = useState(false);

  // Validation logic - Step is valid when approvers are selected
  const isStepValid: boolean = Boolean(
    approverSelection !== null && 
    approverSelection.selectedApprovers.length > 0
  );

  // Check if items are selected for button styling
  const hasApproversSelected = (approverSelection?.selectedApprovers.length || 0) > 0;
  const hasReviewersSelected = (reviewerSelection?.selectedReviewers.length || 0) > 0;

  // Notify parent component of validation status changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isStepValid);
    }
  }, [isStepValid, onValidationChange]);

  // Handle approver selection
  const handleApproverSelectionConfirm = (selection: ApproverSelection) => {
    if (onApproverChange) {
      onApproverChange(selection);
    }
    setShowApproverSelection(false);
  };

  // Handle reviewer selection
  const handleReviewerSelectionConfirm = (selection: ReviewerSelection) => {
    if (onReviewerChange) {
      onReviewerChange(selection);
    }
    setShowReviewerSelection(false);
  };

  // Clear approver selection
  const handleClearApprovers = () => {
    if (onApproverChange) {
      onApproverChange(null);
    }
  };



  // Remove individual approver
  const handleRemoveApprover = (userId: number) => {
    if (approverSelection) {
      const updatedApprovers = approverSelection.selectedApprovers.filter(
        approver => approver.userId !== userId
      );
      
      if (updatedApprovers.length === 0) {
        handleClearApprovers();
      } else {
        const updatedSelection: ApproverSelection = {
          ...approverSelection,
          selectedApprovers: updatedApprovers
        };
        if (onApproverChange) {
          onApproverChange(updatedSelection);
        }
      }
    }
  };

  // Remove individual reviewer
  const handleRemoveReviewer = (userId: number) => {
    if (reviewerSelection) {
      const updatedReviewers = reviewerSelection.selectedReviewers.filter(
        reviewer => reviewer.userId !== userId
      );
      
      const updatedSelection: ReviewerSelection = {
        ...reviewerSelection,
        selectedReviewers: updatedReviewers
      };
      if (onReviewerChange) {
        onReviewerChange(updatedSelection);
      }
    }
  };

  return (
    <div className="step-content">
      {/* Selection Sections */}
      <div className="step2-selection-container">
        
        {/* Approver Selection Section */}
        <div className="selection-section">
          <div className="s2-section-header">
            <span className="s2-section-title">Approver Selection</span>
            {hasApproversSelected && (
              <span className="s2-count-badge">{approverSelection?.selectedApprovers.length} selected</span>
            )}
            <button
              type="button"
              onClick={() => setShowApproverSelection(true)}
              disabled={!userCompanyId}
              className={`selection-btn ${hasApproversSelected ? 'has-items' : ''}`}
            >
              {!approverSelection ? 'Select Approvers' : 'Modify'}
            </button>
          </div>

          {/* Company Warning */}
          {!userCompanyId && (
            <div className="form-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                User company information not available. Please contact support.
              </span>
            </div>
          )}

          {/* Selected Approvers Display */}
          {!approverSelection || approverSelection.selectedApprovers.length === 0 ? (
            <div className="s2-empty-inline">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}>
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
              No approvers selected — click <strong>Select Approvers</strong> to choose who will approve this request
            </div>
          ) : (
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Division</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {approverSelection.selectedApprovers.map((approver) => (
                    <tr key={approver.userId}>
                      <td className="approver-function">
                        <strong>{approverSelection.function?.functionName || ''}</strong>
                      </td>
                      <td className="approver-name">
                        <strong>{approver.userName}</strong>
                      </td>
                      <td className="approver-email">
                        {approver.userEmail}
                      </td>
                      <td className="approver-actions">
                        <button
                          type="button"
                          onClick={() => handleRemoveApprover(approver.userId)}
                          className="simple-remove-btn"
                          title="Remove approver from selection"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reviewer Selection Section */}
        <div className="selection-section">
          <div className="s2-section-header">
            <span className="s2-section-title">Reviewer Selection</span>
            <span className="s2-count-badge" style={{ color: '#9ca3af' }}>Optional</span>
            {hasReviewersSelected && (
              <span className="s2-count-badge">{reviewerSelection?.selectedReviewers.length} selected</span>
            )}
            <button
              type="button"
              onClick={() => setShowReviewerSelection(true)}
              disabled={!regionId}
              className={`selection-btn ${hasReviewersSelected ? 'has-items' : ''}`}
            >
              {!reviewerSelection || reviewerSelection.selectedReviewers.length === 0 ? 'Select Reviewers' : 'Modify'}
            </button>
          </div>

          {/* Region Warning */}
          {!regionId && (
            <div className="form-warning">
              <span className="warning-icon">⚠️</span>
              <span className="warning-text">
                Please select a region in the Basic Information step before choosing reviewers.
              </span>
            </div>
          )}

          {/* Selected Reviewers Display */}
          {!reviewerSelection || reviewerSelection.selectedReviewers.length === 0 ? (
            <div className="s2-empty-inline">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}>
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
              </svg>
              No reviewers selected — click <strong>Select Reviewers</strong> to optionally add a review step before approval
            </div>
          ) : (
            <div className="selected-items-table">
              <table className="selection-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Division</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {reviewerSelection.selectedReviewers.map((reviewer) => (
                    <tr key={reviewer.userId}>
                      <td className="reviewer-company">
                        <strong>{reviewerSelection.company?.companyName || ''}</strong>
                      </td>
                      <td className="reviewer-function">
                        <strong>{reviewerSelection.function?.functionName || ''}</strong>
                      </td>
                      <td className="reviewer-name">
                        <strong>{reviewer.userName}</strong>
                      </td>
                      <td className="reviewer-email">
                        {reviewer.userEmail}
                      </td>
                      <td className="reviewer-actions">
                        <button
                          type="button"
                          onClick={() => handleRemoveReviewer(reviewer.userId)}
                          className="simple-remove-btn"
                          title="Remove reviewer from selection"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* Popups */}
      <ApproverSelectionPopup
        isOpen={showApproverSelection}
        currentSelection={approverSelection}
        userCompanyId={userCompanyId}
        getAccessToken={getAccessToken}
        onClose={() => setShowApproverSelection(false)}
        onConfirm={handleApproverSelectionConfirm}
        onStatusUpdate={onStatusUpdate}
      />

      <ReviewerSelectionPopup
        isOpen={showReviewerSelection}
        currentSelection={reviewerSelection}
        selectedRegionId={regionId || null}
        getAccessToken={getAccessToken}
        onClose={() => setShowReviewerSelection(false)}
        onConfirm={handleReviewerSelectionConfirm}
        onStatusUpdate={onStatusUpdate}
      />
    </div>
  );
};